/**
 * Mock Provider — Development/demo agent controller
 *
 * Simulates streaming responses with thinking, tool calls,
 * and file modifications for testing without an API key.
 */
import type { Workspace } from '../types'
import type {
    AgentConfig,
    AgentRunController,
    TranscriptEvent,
    ResolvedRef,
    FileModification,
} from '../agentTypes'

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

function delay(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) { reject(new DOMException('Aborted', 'AbortError')); return }
        const timer = setTimeout(resolve, ms)
        signal?.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')) }, { once: true })
    })
}

function generateId(): string {
    return Math.random().toString(36).slice(2, 10)
}

// ────────────────────────────────────────────────────────────────
// Mock Scenarios
// ────────────────────────────────────────────────────────────────

interface MockScenario {
    thinking?: string
    chunks: string[]
    toolCall?: {
        name: string
        args: Record<string, unknown>
        result: string
        filesModified?: FileModification[]
        /** Content to write (for write tools) */
        writeContent?: string
        writePath?: string
    }
    afterToolChunks?: string[]
}

function pickScenario(userMessage: string, refs: ResolvedRef[]): MockScenario {
    const lower = userMessage.toLowerCase()

    // List files
    if (lower.includes('list') || lower.includes('arkiv') || lower.includes('filer')) {
        return {
            thinking: 'I\'ll list the files in your workspace so we can see what\'s available.',
            chunks: [
                'Certainly! Let me check your workspace for you.\n\n',
            ],
            toolCall: {
                name: 'secureblob_list_files',
                args: { dirPath: '/' },
                result: 'Found 12 files in /',
            },
            afterToolChunks: [
                'I found several files, including some notes and configuration files. ',
                'Which one would you like to work with?',
            ],
        }
    }

    // If user references a file, simulate reading + summarising
    if (refs.length > 0) {
        const refPath = refs[0].path
        const preview = refs[0].content?.slice(0, 100) ?? '(could not read)'
        return {
            thinking: `Let me read ${refPath} and analyse its contents to give you a helpful summary.`,
            chunks: [
                `I've read **${refPath}**. `,
                'Here\'s a summary:\n\n',
                `- The file contains ${refs[0].size ?? 'unknown'} bytes\n`,
                `- First line preview: \`${preview.split('\n')[0]}\`\n\n`,
            ],
            toolCall: {
                name: 'secureblob_read_text',
                args: { path: refPath, maxBytes: 100000 },
                result: `Read ${refs[0].size ?? 0} bytes from ${refPath}`,
            },
            afterToolChunks: [
                'The file looks well-structured. ',
                'Let me know if you\'d like me to make any changes! 🚀',
            ],
        }
    }

    // "create" or "write" — simulate file creation
    if (lower.includes('create') || lower.includes('write') || lower.includes('save') || lower.includes('skapa')) {
        return {
            thinking: 'I\'ll create a new file based on your request. Let me think about the best content and structure.',
            chunks: [
                'Sure! I\'ll create that file for you.\n\n',
                'Let me put together the content ',
                'and save it to your workspace.\n\n',
            ],
            toolCall: {
                name: 'secureblob_write_text',
                args: { path: '/notes/agent-created.md' },
                result: 'Created file with 156 bytes',
                filesModified: [{ path: '/notes/agent-created.md', action: 'created' }],
                writePath: '/notes/agent-created.md',
                writeContent: `# Agent-Created Note\n\nThis file was created by the SecureBlob Assistant.\n\n## Contents\n\n- Created at ${new Date().toLocaleString()}\n- Based on your request: "${userMessage.slice(0, 50)}"\n\n> This is a demo of the agent's file creation capability.\n`,
            },
            afterToolChunks: [
                '✅ Done! I\'ve created ',
                '`/notes/agent-created.md` ',
                'with the content. ',
                'Click the file link above to view it.',
            ],
        }
    }

    // Default conversational response
    return {
        thinking: 'Let me think about how to best answer this question about your SecureBlob workspace.',
        chunks: [
            'Great question! ',
            'Here\'s what I can help you with:\n\n',
            '1. **Read files** — I can read and summarise any file in your workspace\n',
            '2. **Create files** — I can write new documents, notes, or configs\n',
            '3. **Edit files** — I can modify existing content\n\n',
            '> **Tip:** Use `@/path/to/file` ',
            'to reference a file in your message ',
            'and I\'ll include it as context.\n\n',
            '```javascript\n',
            '// Example: ask me to create a file\n',
            '"Create a summary of @/notes/meeting-notes.md"\n',
            '```\n\n',
            'What would you like to do? 🚀',
        ],
    }
}

// ────────────────────────────────────────────────────────────────
// Mock Controller
// ────────────────────────────────────────────────────────────────

export function createMockRunController(
    config: AgentConfig,
    workspace: Workspace,
): AgentRunController {
    let abortController: AbortController | null = null

    return {
        async *run(userMessage: string, refs: ResolvedRef[]): AsyncIterableIterator<TranscriptEvent> {
            abortController = new AbortController()
            const signal = abortController.signal

            const scenario = pickScenario(userMessage, refs)
            let tokensIn = userMessage.length * 2 // rough estimate
            let tokensOut = 0

            try {
                // 1. Thinking phase
                if (scenario.thinking) {
                    yield { type: 'assistant_thinking', text: scenario.thinking, timestamp: Date.now() }
                    await delay(800 + Math.random() * 600, signal)
                }

                // 2. Initial cost update
                tokensIn += refs.reduce((sum, r) => sum + (r.content?.length ?? 0), 0)
                yield {
                    type: 'cost_update',
                    tokensIn,
                    tokensOut: 0,
                    estimatedCostUsd: tokensIn * 0.000003,
                    timestamp: Date.now(),
                }

                // 3. Stream text chunks
                for (const chunk of scenario.chunks) {
                    await delay(40 + Math.random() * 80, signal)
                    tokensOut += chunk.length
                    yield { type: 'assistant_stream_delta', text: chunk, timestamp: Date.now() }
                }

                // 4. Tool call (if scenario has one)
                if (scenario.toolCall) {
                    const toolId = generateId()

                    // Emit pending
                    yield {
                        type: 'tool_call',
                        id: toolId,
                        name: scenario.toolCall.name,
                        args: scenario.toolCall.args,
                        status: 'pending',
                        timestamp: Date.now(),
                    }

                    await delay(200, signal)

                    // Emit running
                    yield {
                        type: 'tool_call',
                        id: toolId,
                        name: scenario.toolCall.name,
                        args: scenario.toolCall.args,
                        status: 'running',
                        timestamp: Date.now(),
                    }

                    // Actually execute the write if applicable
                    if (scenario.toolCall.writePath && scenario.toolCall.writeContent) {
                        const canWrite = config.tools?.secureBlob?.write !== false
                        if (canWrite) {
                            workspace.fs.writeText(scenario.toolCall.writePath, scenario.toolCall.writeContent)
                        }
                    }

                    await delay(600 + Math.random() * 400, signal)

                    // Emit completed
                    yield {
                        type: 'tool_call',
                        id: toolId,
                        name: scenario.toolCall.name,
                        args: scenario.toolCall.args,
                        status: 'completed',
                        timestamp: Date.now(),
                    }

                    // Emit result
                    yield {
                        type: 'tool_result',
                        id: toolId,
                        name: scenario.toolCall.name,
                        result: scenario.toolCall.result,
                        filesModified: scenario.toolCall.filesModified,
                        timestamp: Date.now(),
                    }
                }

                // 5. After-tool text chunks
                if (scenario.afterToolChunks) {
                    for (const chunk of scenario.afterToolChunks) {
                        await delay(40 + Math.random() * 60, signal)
                        tokensOut += chunk.length
                        yield { type: 'assistant_stream_delta', text: chunk, timestamp: Date.now() }
                    }
                }

                // 6. Final cost update
                yield {
                    type: 'cost_update',
                    tokensIn,
                    tokensOut,
                    estimatedCostUsd: tokensIn * 0.000003 + tokensOut * 0.000015,
                    timestamp: Date.now(),
                }

                // 7. Final message (aggregate all streamed text)
                const allText = [...scenario.chunks, ...(scenario.afterToolChunks ?? [])].join('')
                yield { type: 'assistant_message_final', text: allText, timestamp: Date.now() }

            } catch (err) {
                if (err instanceof DOMException && err.name === 'AbortError') {
                    // Cancelled — don't yield error, let the hook handle status
                    return
                }
                yield { type: 'error', message: String(err), retryable: true, timestamp: Date.now() }
            }
        },

        abort() {
            abortController?.abort()
        },
    }
}
